import React, { useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import TenderProcessTable from "../TenderProcessTable/TenderProcessTable";
import SubStepTracker from "../../components/SubStepTracker/SubStepTracker";
import SaveAsDropBox from "../SaveAsDropBox/SaveAsDropBox";
import { 
    importPprSetFields,
    processProductComparison,
    exportProductComparison,
    reselectPprSetFields,
} from "../../api/api";
import { 
    ImportButton,
    Header,
    SubHeader,
    SourceDocumentsPageContainer,
    GenerateButton,
    ReselectButton,
    ButtonContainer
} from "../TenderProcess/TenderProcess.styles";
import { useTenderFiles } from "./useTenderFiles";
import { useToast } from '../ToastProvider/ToastProvider';

const UploadSourceDocuments = ({ handleFileUpload }) => {
    const fileInputRef = useRef(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            handleFileUpload(files);
        }
    };

    return (
        <>
            <ImportButton data-testid="import-button" onClick={handleImportClick}>
                Import
            </ImportButton>
            <input
                data-testid="file-selector"
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </>
    );
};

const ReviewFiles = ({ handleReselectFiles, handleProcessSpreadsheet, files }) => {

    return (
        <>
            <TenderProcessTable files={files}/>
            <ButtonContainer>
                <ReselectButton data-testid="reselect-button" onClick={handleReselectFiles}>Reselect Files</ReselectButton>
                <GenerateButton data-testid="generate-button" onClick={handleProcessSpreadsheet}>Generate</GenerateButton>
            </ButtonContainer>
        </>
    );
};

const SaveSummarySpreadsheet = ({ handleExportSpreadsheet }) => {
    return (
        <>
            <SaveAsDropBox />
            <ImportButton data-testid="save-to-sharepoint-button" onClick={handleExportSpreadsheet}>
                Save File
            </ImportButton>
        </>
    );
};

const mapSubSteptoNumber = (subStep) => {
    if (subStep == null) return 1;
    return {
        'upload': 1,
        'process': 2,
        'generate': 3
    }[subStep]
}

const ProductComparison = ({tenderId, subStep}) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [step, setStep] = useState(mapSubSteptoNumber(subStep));
    const [loading, setLoading] = useState(false);
    const { files, setFiles } = useTenderFiles({ tenderId, step, setLoading });

    const handleFileUpload = async (selectedFiles) => {
        setLoading(true);
        try {
            const result = await importPprSetFields(tenderId, selectedFiles);

            const data = await result.json();
            setFiles(data.files);
            setStep(2);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file upload:", error);
            showToast('Ensure correct documents are selected.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReselectFiles = async () => {
        setLoading(true);
        try {
            await reselectPprSetFields(tenderId);

            setFiles([]);
            setStep(1);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file reselect:", error);
            showToast('Something went wrong, please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessSpreadsheet = async () => {
        setLoading(true);
        try {
            await processProductComparison(tenderId);
            setStep(3);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet processing:", error);
            showToast('An error occurred during spreadsheet processing.', 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleExportSpreadsheet = async () => {
        setLoading(true);        
        try {
            await exportProductComparison(tenderId);
            navigate(`/${tenderId}/client-comparison`);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet export:", error);
            showToast('An error occurred during spreadsheet export.', 'error');
        } finally {
            setLoading(false);
        }
    }

    const stepMappings = {
        1: {
            title: "Import the PPR Set Fields",
            subtitle: "Please note: The information from the summary sheet will automatically be used.",
            component: <UploadSourceDocuments handleFileUpload={handleFileUpload} />,
        },
        2: {
            title: "Import Complete",
            subtitle: "Ensure the files are correct and click on “Generate”",
            component: <ReviewFiles handleReselectFiles={handleReselectFiles} handleProcessSpreadsheet={handleProcessSpreadsheet} files={files} />,
        },
        3: {
            title: "Generation Completed",
            subtitle: "Your Quote PPR is ready to be exported",
            component: <SaveSummarySpreadsheet handleExportSpreadsheet={handleExportSpreadsheet} />,
        },
    };

    return (
        <SourceDocumentsPageContainer step={step}>
            <SubStepTracker currentStep={step} />
            <Header data-testid="tenderHeader">{stepMappings[step].title}</Header>
            <SubHeader data-testid="tenderSubheader">{stepMappings[step].subtitle}</SubHeader>
            {loading ? (
                <img src="/Icons/jigsaw-loading.gif" alt="Loading" data-testid="loading-screen" />
            ) : (
                stepMappings[step].component
            )}
        </SourceDocumentsPageContainer>
    );
};

export default ProductComparison;